import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';

import { useAuth } from '../../contexts/AuthContext';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import AuthBanner from '../../components/auth/AuthBanner';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [errors, setErrors] = useState({});
  
  const { login, resendVerification } = useAuth();
  const { t, currentLanguage } = usePublicLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const hasShownMessage = useRef(false);

  // Check for redirect message on mount
  useEffect(() => {
    if (hasShownMessage.current) return;
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    if (!message) return;

    if (message === 'verification-required') {
      toast.info(t('auth.messages.verificationRequired'));
      hasShownMessage.current = true;
    } else if (message === 'signup-success') {
      toast.success(t('auth.messages.signupSuccess'));
      hasShownMessage.current = true;
    }

    // Remove one-shot query params to avoid repeated toasts on refresh.
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear field-specific errors on change
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = t('auth.login.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.login.errors.emailInvalid');
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.login.errors.passwordRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setErrors({});

    try {
      const result = await login(formData.email, formData.password, currentLanguage);
      
      if (result.success) {
        if (result.message) {
          toast.warning(result.message);
        }
        
        // Redirect based on the API response
        const redirectTo = result.redirect || '/dashboard';
        navigate(redirectTo);
      } else {
        // Use errorCode for comparison instead of translated messages
        const errorCode = result.errorCode;
        
        // Check if account is not verified
        if (errorCode === 'NOT_VERIFIED') {
          setShowResendVerification(true);
          setUserEmail(formData.email);
          toast.error(result.error);
        } 
        // Check if user not found - show under email field
        else if (errorCode === 'USER_NOT_FOUND') {
          setErrors({ email: result.error });
        } 
        // Check if password incorrect - show under password field
        else if (errorCode === 'INCORRECT_PASSWORD') {
          setErrors({ password: result.error });
        } 
        // For any other error, show in toast
        else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error(t('auth.messages.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const result = await resendVerification(userEmail, currentLanguage);
      if (result.success) {
        toast.success(result.message);
        setShowResendVerification(false);
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error(t('auth.messages.resendError'));
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 auth-login-page">
      <div className="min-h-screen grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="hidden lg:flex">
          <AuthBanner type="login" />
        </div>

        <div className="relative flex items-center justify-center px-4 py-10 sm:px-8 lg:px-14 auth-login-form-wrap">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="relative w-full max-w-xl"
          >
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              onSubmit={handleSubmit}
              noValidate
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 auth-login-form-paper"
            >
              <div className="mb-5">
                <h1 className="text-xl font-semibold text-slate-900 auth-login-title">{t('auth.login.title')}</h1>
                <p className="mt-1 text-sm text-slate-600 auth-login-subtitle">{t('auth.login.subtitle')}</p>
              </div>

              <div className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.login.email')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Mail className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? 'login-email-error' : undefined}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-3 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.email
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder="name@company.com"
                    />
                  </div>
                  {errors.email && (
                    <p id="login-email-error" className="mt-2 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-slate-700 auth-login-label">
                      {t('auth.login.password')}
                    </label>
                    <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:text-indigo-500 auth-login-link">
                      {t('auth.login.forgotPassword')}
                    </Link>
                  </div>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      aria-invalid={!!errors.password}
                      aria-describedby={errors.password ? 'login-password-error' : undefined}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-10 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.password
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 text-slate-500 transition-colors hover:text-slate-700 auth-login-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('auth.password.hide') : t('auth.password.show')}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p id="login-password-error" className="mt-2 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 auth-login-submit"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    {t('auth.login.loginButton')}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              {showResendVerification && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
                >
                  <div className="text-center">
                    <h3 className="text-sm font-semibold text-amber-900">
                      {t('auth.verification.unverifiedAccount')}
                    </h3>
                    <p className="mt-1 text-xs text-amber-700">
                      {t('auth.verification.checkEmailPrompt')}
                    </p>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="mt-2 text-xs font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900"
                    >
                      {t('auth.verification.resend')}
                    </button>
                  </div>
                </motion.div>
              )}

              <div className="my-6 flex items-center gap-3 auth-login-divider">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-500">{t('auth.login.or')}</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <p className="text-center text-sm text-slate-600 auth-login-subtitle">
                {t('auth.login.noAccount')}{' '}
                <Link to="/signup" className="font-semibold text-indigo-600 hover:text-indigo-500 auth-login-link">
                  {t('auth.login.signUp')}
                </Link>
              </p>
            </motion.form>

            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
                ← {t('auth.messages.backToHome')}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
